import {
  React, useState,
} from 'react';
import { PropTypes } from 'prop-types';
import { useProject } from '../../../config/ProjectContext';
import SimpleAlert from '../../../components/utils/SimpleAlert';
import DeployEventsHistoryTablePaginated from '../MachineTelemetry/DeployEventsHistoryTablePaginated';
import useLifeCycleObjectPaginated from '../DeploymentDetails/useLifeCycleObjectPaginated';
import PaginationComponent from '../../../components/Pagination';
import { calculateEndDateTimeIso, calculateStartDateTimeIso, validate } from '../../../utils/dateUtils';
import JSONBuilderFilters, { valueTypes } from '../../../components/utils/JSONBuilderFilters';
import moment from 'moment';

const jsonColumns = [
  {
    id: 'deployid', label: 'Deploy ID', valueType: valueTypes.STRICT_STRING_EQUAL, values: [],
  },
  {
    id: 'deploytype', label: 'Type', valueType: valueTypes.STRICT_STRING_EQUAL, values: [],
  },
  {
    id: 'fw', label: 'Previous Firmware Version', valueType: valueTypes.STRICT_STRING_EQUAL, values: [],
  },
  {
    id: 'hw', label: 'Hardware Version', valueType: valueTypes.STRICT_STRING_EQUAL, values: [],
  },
];

export default function DeviceInfo({ deviceSerial }) {
  const { project } = useProject();

  const [alertOpen, setAlertOpen] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(moment().add(-7, 'days'));
  const [selectedStartTime, setSelectedStartTime] = useState(moment().add(-7, 'days').format('HH:mm'));
  const [selectedEndDate, setSelectedEndDate] = useState(moment());
  const [selectedEndTime, setSelectedEndTime] = useState(moment().format('23:59'));

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const [selectedFilters, setSelectedFilters] = useState('');
  const [filterError, setFilterError] = useState('');

  const alertText = '';

  const {
    data, isLoading, error, refetch,
  } = useLifeCycleObjectPaginated(
    'machineassetsdetail',
    project,
    deviceSerial,
    '{}',
    selectedFilters,
    selectedStartDate,
    selectedEndDate,
    selectedStartTime,
    selectedEndTime,
    currentPage,
    pageSize,
  );

  const deploymentData = data?.data;
  const pagination = data?.pagination;

  const handlePageChange = (event, value) => {
    setCurrentPage(value - 1);
  };

  const handleSelectChange = (event) => {
    setPageSize(event.target.value);
  };

  const onSearch = (event) => {
    event.preventDefault();
    if (!validate(selectedStartDate, selectedEndDate, selectedStartTime, selectedEndTime, 90)) return;
    setCurrentPage(0);
    if (refetch) {
      refetch();
    }
  };

  return (
    <div className="space-y-4 p-4">
      {(error || alertText) && <SimpleAlert open={alertOpen} setOpen={setAlertOpen} header="Error" body={error?.message || alertText} />}

      <JSONBuilderFilters
        id="DeploymentEventsHistory"
        setSelectedFilters={setSelectedFilters}
        selectedStartDate={selectedStartDate}
        setSelectedStartDate={setSelectedStartDate}
        selectedStartTime={selectedStartTime}
        setSelectedStartTime={setSelectedStartTime}
        selectedEndDate={selectedEndDate}
        setSelectedEndDate={setSelectedEndDate}
        selectedEndTime={selectedEndTime}
        setSelectedEndTime={setSelectedEndTime}
        filtersVisible
        deviceSerialVisible={false}
        jsonColumns={jsonColumns}
        onSearch={onSearch}
        setFilterError={setFilterError}
        filterError={filterError}
        maxDaysBetweenDates={90}
        filtersComponents={[]}
      />

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          <p className="ml-2 text-gray-700">Loading data...</p>
        </div>
      ) : (
        <div className="w-full">
          <DeployEventsHistoryTablePaginated
            dataList={deploymentData}
            filters={selectedFilters}
            loading={isLoading}
            dateIni={calculateStartDateTimeIso(selectedStartDate, selectedStartTime)}
            dateEnd={calculateEndDateTimeIso(selectedEndDate, selectedEndTime)}
            object={`machineassetsdetail/${project.code}/${deviceSerial}`}
            params={{}}
            id="DeploymentDetails"
            project={project}
          />
          {pagination && (
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
              <PaginationComponent
                pageSize={pageSize}
                handleSelectChange={handleSelectChange}
                pagination={pagination}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DeviceInfo.propTypes = {
  deviceSerial: PropTypes.string.isRequired,
};
